import { z } from "zod";

/**
 * Configurações › Pacientes: como cada clínica quer o cadastro, a anamnese e a ficha.
 *
 * Sem nada salvo (`null`), tudo se comporta como antes desta configuração existir — é o padrão.
 * O resolvedor é tolerante (como o das etiquetas): o que vier quebrado ou desconhecido cai no
 * padrão, e campo novo entra já com o padrão, sem migrar ninguém.
 */

/** Itens do cadastro que a clínica configura. Nome é sempre obrigatório; tags, observações e foto
 *  ficam fora. Endereço, contato de emergência e convênio são um item só cada. */
export const CAMPOS_CONFIGURAVEIS_DO_CADASTRO = [
  "phone",
  "birthDate",
  "sex",
  "document",
  "email",
  "whatsapp",
  "landline",
  "contatoDeEmergencia",
  "endereco",
  "profession",
  "howDidMeet",
  "motherName",
  "convenio",
] as const;
export type CampoConfiguravel = (typeof CAMPOS_CONFIGURAVEIS_DO_CADASTRO)[number];

export const ROTULO_DO_CAMPO_CONFIGURAVEL: Record<CampoConfiguravel, string> = {
  phone: "Celular",
  birthDate: "Nascimento",
  sex: "Sexo",
  document: "CPF",
  email: "E-mail",
  whatsapp: "WhatsApp",
  landline: "Telefone fixo",
  contatoDeEmergencia: "Contato de emergência",
  endereco: "Endereço",
  profession: "Profissão",
  howDidMeet: "Como conheceu",
  motherName: "Nome da mãe",
  convenio: "Convênio",
};

/** As colunas de Patient que cada item exige quando é obrigatório (complemento e número da
 *  carteirinha nunca são exigidos). */
export const COLUNAS_DO_CAMPO: Record<CampoConfiguravel, string[]> = {
  phone: ["phone"],
  birthDate: ["birthDate"],
  sex: ["sex"],
  document: ["document"],
  email: ["email"],
  whatsapp: ["whatsapp"],
  landline: ["landline"],
  contatoDeEmergencia: ["contatoDeEmergenciaNome", "contatoDeEmergenciaTelefone"],
  endereco: ["zipCode", "address", "addressNumber", "neighborhood", "city", "state"],
  profession: ["profession"],
  howDidMeet: ["howDidMeet"],
  motherName: ["motherName"],
  convenio: ["insurancePlanName"],
};

/** O que o autocadastro (`/p/`) pergunta. Obrigatório fora daqui a equipe completa depois. */
export const CAMPOS_DO_AUTOCADASTRO: CampoConfiguravel[] = ["phone", "birthDate", "sex", "document", "email", "endereco", "profession", "howDidMeet"];

export const EXIGENCIAS_DO_CAMPO = ["opcional", "obrigatorio", "oculto"] as const;
export type ExigenciaDoCampo = (typeof EXIGENCIAS_DO_CAMPO)[number];

/** Abas da ficha que a clínica pode esconder. Início e Dados ficam sempre. */
export const ABAS_CONFIGURAVEIS_DA_FICHA = [
  "anamnese",
  "odontograma",
  "tratamentos",
  "consultas",
  "proteses",
  "arquivos",
  "financeiro",
  "historico",
] as const;
export type AbaConfiguravel = (typeof ABAS_CONFIGURAVEIS_DA_FICHA)[number];

export const VALIDADES_DO_LINK_DA_ANAMNESE = [1, 3, 7, 15, 30] as const;
/** 0 = nunca vence (o padrão). */
export const RENOVACOES_DA_ANAMNESE = [0, 6, 12, 24] as const;

/** O texto que ia fixo no envio. Só marcadores sem dado de saúde: a mensagem sai por WhatsApp. */
export const MENSAGEM_PADRAO_DA_ANAMNESE =
  "Olá, {nome}! Para agilizar sua consulta, preencha sua ficha de saúde neste link (vale até {validade}). Vamos pedir sua data de nascimento para abrir: {link}";
export const MARCADORES_DA_MENSAGEM = ["{nome}", "{clinica}", "{validade}", "{link}"] as const;

export type ConfiguracaoDePacientes = {
  cadastro: { campos: Record<CampoConfiguravel, ExigenciaDoCampo> };
  anamnese: {
    validadeDoLinkDias: (typeof VALIDADES_DO_LINK_DA_ANAMNESE)[number];
    renovarACadaMeses: (typeof RENOVACOES_DA_ANAMNESE)[number];
    lembrarAoAgendar: boolean;
    mensagem: string;
  };
  ficha: { abasOcultas: AbaConfiguravel[] };
};

const umDe = <T extends readonly (string | number)[]>(lista: T, v: unknown, padrao: T[number]): T[number] =>
  (lista as readonly unknown[]).includes(v) ? (v as T[number]) : padrao;

const mensagemValida = (m: unknown): m is string =>
  typeof m === "string" && m.trim().length >= 10 && m.length <= 500 && m.includes("{link}");

/** Sempre um objeto completo: `null` ou lixo dá o padrão de hoje. */
export function resolverConfiguracaoDePacientes(salvo: unknown): ConfiguracaoDePacientes {
  const s = (salvo && typeof salvo === "object" ? salvo : {}) as Record<string, any>;
  const campos = Object.fromEntries(
    CAMPOS_CONFIGURAVEIS_DO_CADASTRO.map((c) => [c, umDe(EXIGENCIAS_DO_CAMPO, s.cadastro?.campos?.[c], "opcional")]),
  ) as Record<CampoConfiguravel, ExigenciaDoCampo>;
  const a = s.anamnese ?? {};
  const ocultas = Array.isArray(s.ficha?.abasOcultas) ? s.ficha.abasOcultas : [];
  return {
    cadastro: { campos },
    anamnese: {
      validadeDoLinkDias: umDe(VALIDADES_DO_LINK_DA_ANAMNESE, a.validadeDoLinkDias, 7),
      renovarACadaMeses: umDe(RENOVACOES_DA_ANAMNESE, a.renovarACadaMeses, 0),
      lembrarAoAgendar: a.lembrarAoAgendar === true,
      mensagem: mensagemValida(a.mensagem) ? a.mensagem.trim() : MENSAGEM_PADRAO_DA_ANAMNESE,
    },
    ficha: { abasOcultas: ABAS_CONFIGURAVEIS_DA_FICHA.filter((aba) => ocultas.includes(aba)) },
  };
}

/** O que a tela manda pra salvar. `null` volta ao padrão. */
export const configuracaoDePacientesSchema = z
  .object({
    cadastro: z.object({ campos: z.record(z.enum(CAMPOS_CONFIGURAVEIS_DO_CADASTRO), z.enum(EXIGENCIAS_DO_CAMPO)) }),
    anamnese: z.object({
      validadeDoLinkDias: z.number().refine((v) => (VALIDADES_DO_LINK_DA_ANAMNESE as readonly number[]).includes(v), "Validade inválida"),
      renovarACadaMeses: z.number().refine((v) => (RENOVACOES_DA_ANAMNESE as readonly number[]).includes(v), "Renovação inválida"),
      lembrarAoAgendar: z.boolean(),
      mensagem: z
        .string()
        .trim()
        .min(10, "Mensagem muito curta")
        .max(500, "Mensagem muito longa")
        .refine((m) => m.includes("{link}"), "A mensagem precisa ter {link}, senão o paciente não recebe o endereço"),
    }),
    ficha: z.object({ abasOcultas: z.array(z.enum(ABAS_CONFIGURAVEIS_DA_FICHA)).max(ABAS_CONFIGURAVEIS_DA_FICHA.length) }),
  })
  .nullable();
export type ConfiguracaoDePacientesInput = z.infer<typeof configuracaoDePacientesSchema>;

const vazio = (v: unknown) => v == null || (typeof v === "string" && v.trim() === "");

/**
 * Os itens obrigatórios que faltam nos dados de um paciente. `semCpf`: estrangeiro, sem CPF — a
 * exigência de CPF não vale pra ele.
 */
export function faltandoNoCadastro(
  config: Pick<ConfiguracaoDePacientes, "cadastro">,
  dados: Record<string, unknown>,
  opcoes: { semCpf?: boolean; apenas?: CampoConfiguravel[] } = {},
): CampoConfiguravel[] {
  return CAMPOS_CONFIGURAVEIS_DO_CADASTRO.filter(
    (c) =>
      config.cadastro.campos[c] === "obrigatorio" &&
      (!opcoes.apenas || opcoes.apenas.includes(c)) &&
      !(c === "document" && opcoes.semCpf) &&
      COLUNAS_DO_CAMPO[c].some((col) => vazio(dados[col])),
  );
}

/** "Preencha: CPF, Nascimento." */
export function mensagemDeFaltando(faltando: CampoConfiguravel[]): string {
  return `Preencha: ${faltando.map((c) => ROTULO_DO_CAMPO_CONFIGURAVEL[c]).join(", ")}.`;
}

/** Itens obrigatórios que seriam apagados por uma atualização (valor `null` ou vazio). */
export function obrigatoriosApagados(config: Pick<ConfiguracaoDePacientes, "cadastro">, dados: Record<string, unknown>): CampoConfiguravel[] {
  return CAMPOS_CONFIGURAVEIS_DO_CADASTRO.filter(
    (c) => config.cadastro.campos[c] === "obrigatorio" && COLUNAS_DO_CAMPO[c].some((col) => col in dados && vazio(dados[col])),
  );
}

/** Anamnese respondida há mais de `meses` (0 = nunca vence). */
export function anamneseVencida(respondidaEm: Date | string | null | undefined, meses: number, agora = new Date()): boolean {
  if (!meses || !respondidaEm) return false;
  const limite = new Date(respondidaEm);
  limite.setMonth(limite.getMonth() + meses);
  return limite.getTime() <= agora.getTime();
}

export function montarMensagemDaAnamnese(modelo: string, v: { nome: string; clinica: string; validade: string; link: string }): string {
  return modelo
    .replaceAll("{nome}", v.nome)
    .replaceAll("{clinica}", v.clinica)
    .replaceAll("{validade}", v.validade)
    .replaceAll("{link}", v.link);
}
